import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { DeepRequired } from '@app/utils/deep-required';
import { Observable } from 'rxjs';
import { AbstractControlTyped } from '../../types/typed-forms';

/**
 * Set value of the given control and mark it as dirty and touched.
 * @param control Control for which the value is set
 * @param value Value to set
 * @param validate Runs updateValueAndValidity if true (default true)
 */
export function setValue<T>(
  control: AbstractControlTyped<T>,
  value: T,
  { validate = true }: Options = {}
): void {
  control.setValue(value);
  control.markAsDirty();
  control.markAsTouched();

  if (validate) {
    control.updateValueAndValidity();
  }
}

export const FormHelper = {
  /**
   * Enable or disable the given control based on the given predicate.
   * @param control Form control to enable or disable.
   * @param predicate If true the control is enabled, if false it is disabled. If undefined the state is not changed.
   */
  setDisabled(control: AbstractControl, predicate: boolean): void {
    if (predicate === true) {
      control.disable();
    } else if (predicate === false) {
      control.enable();
    }
  },

  /**
   * Enable or disable the given control based on the value of enabled$.
   * @param control Form control to enable or disable.
   * @param disabled$ If true or false then the control will be enabled or disabled accordingly.
   * If undefined the state is not changed.
   * @param initially If true or false then the control will be enabled or disabled immediately.
   * If undefined the state is not changed.
   */
  setDisabled$({ control, initially, predicate$ }: EnabledOptions): void {
    predicate$.subscribe((predicate) => FormHelper.setDisabled(control, predicate));

    FormHelper.setDisabled(control, initially);
  },

  /**
   * Enable or disable the given control based on the given predicate.
   * @param control Form control to enable or disable.
   * @param predicate If true the control is enabled, if false it is disabled. If undefined the state is not changed.
   */
  setEnabled<T extends AbstractControl>(control: T, predicate: boolean): void {
    if (predicate === true) {
      control.enable();
    } else if (predicate === false) {
      control.disable();
    }
  },

  /**
   * Enable or disable the given control based on the value of enabled$.
   * @param control Form control to enable or disable.
   * @param enabled$ If true or false then the control will be enabled or disabled accordingly.
   * If undefined the state is not changed.
   * @param initially If true or false then the control will be enabled or disabled immediately.
   * If undefined the state is not changed.
   */
  setEnabled$({ control, initially, predicate$ }: EnabledOptions): void {
    predicate$.subscribe((predicate) => FormHelper.setEnabled(control, predicate));

    FormHelper.setEnabled(control, initially);
  },
};

interface EnabledOptions {
  control: AbstractControl;
  initially?: boolean;
  predicate$: Observable<boolean>;
}

interface Options {
  validate?: boolean;
}

export type FormValue<T> = {
  [P in keyof T]?: ControlValue<T[P]>;
};

type ControlValue<T> =
  // Check if `T` is of type FormControl<Whatever>.
  // Return the type `Whatever` if it is a FormControl.
  T extends FormControl<infer U>
    ? U
    : // Check if `T` is of type FormGroup<Whatever> if it is not of type FormControl<Whatever>.
      // Return `FormValue<Whatever>` if it is a FormGroup (FormValue<Whatever> to deeply unpack the value).
      T extends FormGroup<infer U>
      ? FormValue<U>
      : // Check if `T` is of type FormArray<Whatever> if it is not of type FormControl<Whatever> or FormGroup<Whatever>.
        // Return `FormValue<Whatever>` if it is a FormArray (FormValue<Whatever> to deeply unpack the value).
        T extends FormArray<infer U>
        ? FormValue<U>
        : never;

export type RequiredFormValue<T> = DeepRequired<FormValue<T>>;
